import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  DayBook,
  OrderPayments,
  OutstandingPage,
  Payment,
  PaymentResult,
  RecordPaymentInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  useQueueableMutation,
  type QueueableMutationResult,
} from '../offline/use-queueable-mutation.js';
import { orderKeys } from '../orders/use-orders.js';
import {
  getDayBook,
  getOrderPayments,
  getOutstanding,
  reversePayment,
  type OutstandingParams,
} from './payments-api.js';

/**
 * The money layer.
 *
 * Recording a payment goes through the DURABLE QUEUE and reversing one does not, which is the
 * one judgement call in this file. A shop takes cash in a basement changing room with no
 * signal: that write must survive, and the server dedupes the replay on its idempotency key,
 * so queueing it loses nothing and losing it would cost the shop real money. A reversal is the
 * opposite — it is an owner correcting the record, usually while looking at it, and a
 * correction that silently applies twenty minutes later is worse than one that fails loudly
 * now.
 *
 * Every write invalidates orders as well as payments: an order's balance, the unpaid tab's
 * count and the outstanding list are all downstream of one entry landing.
 */

export const paymentKeys = {
  all: ['payments'] as const,
  order: (orderId: string) => ['payments', 'order', orderId] as const,
  dayBook: (date: string | null) => ['payments', 'day-book', date] as const,
  outstanding: (params: OutstandingParams) => ['payments', 'outstanding', params] as const,
};

/** Query keys a payment moves, for both the direct and the replayed path. */
const INVALIDATED_BY_A_PAYMENT: string[][] = [
  ['payments'],
  ['orders'],
  ['alterations'],
  ['customers'],
];

export function useOrderPayments(orderId: string | null): UseQueryResult<OrderPayments> {
  const client = useApiClient();
  return useQuery({
    queryKey: paymentKeys.order(orderId ?? ''),
    queryFn: () => getOrderPayments(client, orderId as string),
    enabled: orderId !== null,
  });
}

export function useDayBook(date: string | null): UseQueryResult<DayBook> {
  const client = useApiClient();
  return useQuery({
    queryKey: paymentKeys.dayBook(date),
    queryFn: () => getDayBook(client, date ?? undefined),
    placeholderData: (previous) => previous,
  });
}

export function useOutstanding(params: OutstandingParams): UseQueryResult<OutstandingPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: paymentKeys.outstanding(params),
    queryFn: () => getOutstanding(client, params),
    placeholderData: (previous) => previous,
  });
}

export interface RecordPaymentVariables extends RecordPaymentInput {
  /** For the pending-writes list, so a queued payment reads as money and not as a URL. */
  label: string;
}

/**
 * Taking money against whatever `paymentsPath` names — an order's ledger
 * (`orderPaymentsPath`) or an alteration's (`alterationPaymentsPath`). Both answer with the entry
 * that was written, which is all a caller reads; both go through the durable queue.
 */
export function useRecordPayment(
  paymentsPath: string,
): UseMutationResult<
  QueueableMutationResult<{ recorded: Payment }>,
  unknown,
  RecordPaymentVariables
> {
  const queryClient = useQueryClient();

  return useQueueableMutation<{ recorded: Payment }, RecordPaymentVariables>({
    buildRequest: ({ label, ...input }) => ({
      method: 'POST',
      path: paymentsPath,
      body: input,
      labelKey: 'payments.queuedWrite',
      labelParams: { amount: label },
      invalidate: INVALIDATED_BY_A_PAYMENT,
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      void queryClient.invalidateQueries({ queryKey: orderKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['alterations'] });
      // A payment moves the customer's lifetime value, which the customer list sorts by.
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useReversePayment(
  orderId: string,
): UseMutationResult<PaymentResult, unknown, { paymentId: string; reason: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: string; reason: string }) =>
      reversePayment(client, orderId, paymentId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      void queryClient.invalidateQueries({ queryKey: orderKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
